/* ─── Container components: Card, Framed, Modal, Tabs ─── */

import { useCallback, useEffect, useState, type ReactNode } from 'react';

import { useArtifactSendEvent, useArtifactDispatch } from '../store';
import { useValueClock, type VectorClock } from '../useValueClock';

// ─── Card ───────────────────────────────────────────────────────────────

export function ArtifactCard({
  child,
  title,
}: {
  child?: ReactNode;
  title?: string;
}) {
  return (
    <div className="a-card">
      {title && <h3 className="a-card-title">{title}</h3>}
      <div className="a-card-body" style={title ? { paddingTop: 32 } : undefined}>{child}</div>
    </div>
  );
}

// ─── Framed ─────────────────────────────────────────────────────────────

export function ArtifactFramed({
  child,
  title,
}: {
  child?: ReactNode;
  title?: string | null;
}) {
  return (
    <fieldset className="a-framed">
      {title && <legend className="a-framed-title">{title}</legend>}
      <div className="a-framed-body">{child}</div>
    </fieldset>
  );
}

// ─── Modal ──────────────────────────────────────────────────────────────

export function ArtifactModal({
  trigger,
  content,
}: {
  trigger?: ReactNode;
  content?: ReactNode;
}) {
  const [open, setOpen] = useState(false);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    },
    [],
  );

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [open, handleKeyDown]);

  return (
    <>
      <div onClick={() => setOpen(true)} style={{ cursor: 'pointer' }}>
        {trigger}
      </div>

      {open && (
        <div className="a-modal-backdrop" onClick={() => setOpen(false)}>
          <div className="a-modal-content" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="a-modal-close"
              onClick={() => setOpen(false)}
              aria-label="Close"
            >
              ×
            </button>
            {content}
          </div>
        </div>
      )}
    </>
  );
}

// ─── Tabs ───────────────────────────────────────────────────────────────

export function ArtifactTabs({
  children,
  value: valueProp,
  __node_id,
  __clock,
}: {
  children?: Record<string, ReactNode>;
  value?: string;
  __node_id?: string;
  __clock?: VectorClock;
}) {
  const sendEvent = useArtifactSendEvent();
  const dispatch = useArtifactDispatch();
  const tabs = children ?? {};
  const tabNames = Object.keys(tabs);
  const [value, setValue] = useState(valueProp ?? tabNames[0] ?? '');
  const clock = useValueClock(__clock);

  useEffect(() => {
    if (!clock.isFresh()) return;
    clock.accept();
    if (valueProp != null) setValue(valueProp);
    const v = valueProp ?? tabNames[0] ?? '';
    if (__node_id) dispatch({ type: 'SET_LIVE_VALUE', cuid: Number(__node_id), name: 'value', value: v });
  }, [__clock, valueProp, __node_id, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTabChange = (name: string) => {
    setValue(name);
    if (__node_id) {
      dispatch({ type: 'SET_LIVE_VALUE', cuid: Number(__node_id), name: 'value', value: name });
      const index = tabNames.indexOf(name);
      sendEvent(__node_id, 'change', { kind: 'TabChanged', value: name, index, _clock: clock.bump() });
    }
  };

  if (tabNames.length === 0) return null;

  return (
    <div className="a-tabs">
      <div className="a-tabs-bar">
        {tabNames.map((name) => (
          <button
            key={name}
            type="button"
            className={`a-tabs-tab ${name === value ? 'a-tabs-tab-active' : ''}`}
            onClick={() => handleTabChange(name)}
          >
            {name}
          </button>
        ))}
      </div>
      <div className="a-tabs-content">
        {tabs[value]}
      </div>
    </div>
  );
}
